import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "@/i18n";
import { parseJoinCode } from "../utils/quickLoginQr";

/**
 * JoinPage - where a scanned family QR lands.
 *
 * Deliberately NOT an automatic login. The code arrives in a URL that could
 * have been forwarded, screenshotted or scanned by accident, so the last step
 * is always a deliberate tap by the person holding the phone. The code is
 * still single-use and expires in ten minutes server-side.
 */
export default function JoinPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { quickLogin, user } = useAuth();
  const { t } = useTranslation();

  const [code] = useState(() => parseJoinCode(params.get("code")));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) navigate("/", { replace: true });
  }, [user, navigate]);

  const join = async () => {
    if (!code || busy) return;
    setBusy(true);
    setError("");
    try {
      await quickLogin(code);
      navigate("/", { replace: true });
    } catch (e) {
      setError(e?.response?.data?.detail || t("join.failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[var(--bg-primary)]">
      <div className="w-full max-w-sm text-center">
        <h1 className="text-2xl font-bold mb-2">{t("join.title")}</h1>

        {!code ? (
          <>
            <p className="text-sm text-gray-500 mb-6">{t("join.noCode")}</p>
            <button
              onClick={() => navigate("/login", { replace: true })}
              className="w-full py-3 rounded-full border-2 border-black dark:border-white font-semibold"
            >
              {t("join.goToLogin")}
            </button>
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-1">{t("join.subtitle")}</p>
            <p className="text-3xl font-bold tracking-[0.3em] my-5" data-testid="join-code">
              {code}
            </p>
            {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
            <button
              onClick={join}
              disabled={busy}
              className="w-full py-3 rounded-full bg-black text-white dark:bg-white dark:text-black font-semibold disabled:opacity-50"
              data-testid="join-confirm"
            >
              {busy ? t("join.joining") : t("join.confirm")}
            </button>
            <p className="text-[11px] text-gray-400 mt-4">{t("join.expiry")}</p>
          </>
        )}
      </div>
    </div>
  );
}
